import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { WidgetContainer, WidgetContainerContext } from '@/core/widgets/export';
import {
  Alert,
  SocialMediaNewsPreferences,
  SocialPost,
} from '@/modules/market-signals/model/social-media';
import { getMatchedAlerts } from '@/modules/market-signals/util/social-media-news-utils';
import socialPostApi, {
  parseIncomingSocialPost,
} from '@/modules/market-signals/api/social-post-api';
import { useSubscription } from '@/core/messaging/export';
import AlertManager from './alert-manager';
import SocialMediaPostItem from './social-media-post-item';
import debounce from 'lodash/debounce';

export function showDesktopNotification(title: string, body?: string) {
  if (Notification.permission === 'granted') {
    new Notification(title, { body });
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        new Notification(title, { body });
      }
    });
  }
}

export function checkAndPlayAlertSoundsForPost(
  postContent: string,
  alerts: Alert[],
  smartMatching = false,
) {
  const matchedAlerts = getMatchedAlerts(postContent, alerts, smartMatching);
  if (matchedAlerts.length === 0) return;

  const soundsToPlay = new Set<string>();
  const matchedWords: string[] = [];

  for (const alert of matchedAlerts) {
    if (alert.sound) {
      soundsToPlay.add(alert.sound);
    }
    matchedWords.push(alert.word);
  }

  showDesktopNotification(
    `AF Social - Alert Words In Post: ${matchedWords.join(', ')}`,
    postContent,
  );

  for (const sound of soundsToPlay) {
    console.error('Playing sound', sound);
    new Audio(`/sounds/${sound}.mp3`).play().catch(() => {});
  }
}

function isOnlyLinkText(text: string): boolean {
  return /^(https?:\/\/[^\s]+)$/.test(text.trim());
}

export default function SocialMediaNews() {
  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);
  const [feed, setFeed] = useState<SocialPost[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>(
    widgetContainer.getPreferences<SocialMediaNewsPreferences>()?.alerts || [],
  );
  const [showModal, setShowModal] = useState(false);
  // Owned by the header's Filter button (SocialMediaNewsWidget.headerActions), which lives
  // in a separate part of the tree — derived from preferences so both stay in sync.
  const preferences = widgetContainer.getPreferences<SocialMediaNewsPreferences>();
  const hideMediaOnlyPosts = preferences?.hideMediaOnlyPosts ?? false;
  const collapseMedia = preferences?.collapseMedia ?? false;
  const [expandedMedia, setExpandedMedia] = useState<Record<string, boolean>>({});
  const [smartMatching, setSmartMatching] = useState(
    widgetContainer.getPreferences<SocialMediaNewsPreferences>()?.smartMatching || false,
  );

  const postIdsRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false);
  const lastLoadTimeRef = useRef<number>(0);
  const alertsRef = useRef<Alert[]>(alerts);
  const smartMatchingRef = useRef<boolean>(smartMatching);
  const newPostsRef = useRef<SocialPost[]>([]);

  useEffect(() => {
    if (!collapseMedia) setExpandedMedia({});
  }, [collapseMedia]);

  // test effect
  /*
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const interval = setInterval(() => {
      const fakeId = 'fake-' + Date.now();
      const post = createFakePost(fakeId);

      if (!postIdsRef.current.has(post.id)) {
        postIdsRef.current.add(post.id);
        checkAndPlayAlertSoundsForPost(post.content, alerts, smartMatching);
        setFeed(prev =>
          [post, ...prev].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
        );
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [alerts, smartMatching]);
  */

  const loadFeed = useCallback(async (page = 0) => {
    if (isLoadingRef.current) return;

    const now = Date.now();
    const waitTime = Math.max(0, 3000 - (now - lastLoadTimeRef.current));
    isLoadingRef.current = true;

    if (waitTime > 0) await new Promise(resolve => setTimeout(resolve, waitTime));

    lastLoadTimeRef.current = Date.now();

    try {
      page === 0 ? setLoading(true) : setLoadingMore(true);
      const rawPosts = await socialPostApi.getHistory('instant-social', page.toString());
      const parsed = rawPosts.map(parseIncomingSocialPost);
      const newPosts = parsed.filter(post => !postIdsRef.current.has(post.id));

      setFeed(prev =>
        [...prev, ...newPosts].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
      );
      newPosts.forEach(post => postIdsRef.current.add(post.id));
    } catch (error) {
      console.error('Failed to fetch feed:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    loadFeed(0);
  }, [loadFeed]);

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  useEffect(() => {
    smartMatchingRef.current = smartMatching;
  }, [smartMatching]);

  useEffect(() => {
    for (const post of newPostsRef.current) {
      postIdsRef.current.add(post.id);
    }
    newPostsRef.current = [];
  }, [feed]);

  useSubscription<any>('/topic/instant-social', rawData => {
    try {
      const post = parseIncomingSocialPost(rawData);
      if (postIdsRef.current.has(post.id)) {
        console.error('Skipping duplicate post:', post);
        return;
      }

      checkAndPlayAlertSoundsForPost(post.content, alertsRef.current, smartMatchingRef.current);
      setFeed(prev => {
        const exists = prev.some(p => p.id === post.id);
        if (exists) return prev;

        const updated = [post, ...prev].sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
        );
        newPostsRef.current.push(post);
        return updated;
      });
    } catch (err) {
      console.error('Failed to parse incoming post:', rawData, err);
    }
  });

  useEffect(() => {
    const handler = debounce(() => {
      const container = containerRef.current;
      if (!container || loading || loadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const threshold = 0.7;
      if ((scrollTop + clientHeight) / scrollHeight >= threshold) {
        const nextPage = Math.floor(feed.length / 10);
        loadFeed(nextPage);
      }
    }, 200);

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handler);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handler);
      }
      handler.cancel();
    };
  }, [loading, loadingMore, feed.length, loadFeed]);

  useEffect(() => {
    widgetContainer.updatePreferences<SocialMediaNewsPreferences>({ alerts, smartMatching });
    // eslint-disable-next-line
  }, [alerts, smartMatching]);

  const isOnlyMediaOrLink = (post: SocialPost): boolean => {
    return (
      (!post.content && post.mediaAttachments && post.mediaAttachments?.length > 0) ||
      isOnlyLinkText(post.content)
    );
  };

  const handleSaveAlerts = (updatedAlerts: Alert[]) => setAlerts(updatedAlerts);

  return (
    <div className="position-relative h-100">
      {/* Header */}
      <div className="position-sticky p-1 border-bottom" style={{ zIndex: 10 }}>
        <div className="d-flex justify-content-center align-items-center gap-2 mb-1">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faBell} className="me-1 mb-1" /> Alerts
          </Button>
        </div>
      </div>

      <div ref={containerRef} className="h-100 overflow-auto pt-2 pb-5 container-responsive-text">
        {loading ? (
          <div className="text-center mt-3">
            <Spinner animation="border" />
          </div>
        ) : (
          feed.map(post => {
            if (hideMediaOnlyPosts && isOnlyMediaOrLink(post)) return null;

            return (
              <SocialMediaPostItem
                key={post.id}
                post={post}
                alerts={alerts}
                collapseMedia={collapseMedia}
                expanded={!!expandedMedia[post.id]}
                onToggleExpand={() =>
                  setExpandedMedia(prev => ({ ...prev, [post.id]: !prev[post.id] }))
                }
                smartMatching={smartMatching}
              />
            );
          })
        )}
      </div>

      <AlertManager
        show={showModal}
        alerts={alerts}
        onClose={() => setShowModal(false)}
        onChangeAlerts={handleSaveAlerts}
        smartMatching={smartMatching}
        setSmartMatching={setSmartMatching}
      />
    </div>
  );
}
